import type { UseFormReturn } from "react-hook-form";
import { useMutation } from "@tanstack/react-query";
import type { TFunction } from "i18next";
import { useAtom } from "jotai";
import { toast } from "sonner";
import { stepDataStateAtom } from "@/state/formSession";
import { STEP_KEYS, type FormStep5CustomerResponse, type StepKey } from "@/types/Form";
import type { MultiStepFormType, StepConfig } from "@/utils/formConfig";
import { hasDirty } from "@/utils/validation";
import { CalculationsApi } from "@/api/CalculationsApi";
import { CustomerApi } from "@/api/CustomerApi";
import { FormApi } from "@/api/FormApi";

export function useUpsertStepMutation(
  stepConfigs: Record<string, StepConfig>,
  formId: string | null,
  customerId: string | null,
  setFormId: (id: string) => void,
  setCustomerId: (id: string) => void,
  form: UseFormReturn<MultiStepFormType>,
  t: TFunction,
) {
  const [stepDataState, setStepDataState] = useAtom(stepDataStateAtom);

  return useMutation({
    mutationFn: async (vars: { stepKey: StepKey; options?: { skipCalculations?: boolean } }) => {
      const { stepKey, options } = vars;
      const config = stepConfigs[stepKey];
      if (stepKey === "summary") return;
      const createdNow = !formId;
      const ensureFormId = async () => {
        if (formId) return formId;
        const created = await FormApi.create();
        setFormId(created.id);
        setStepDataState(prev => {
          const next = { ...prev };
          STEP_KEYS.forEach(key => {
            if (key !== "summary") next[key] = "missing";
          });
          return next;
        });
        return created.id;
      };

      const shouldPost = (key: StepKey) => createdNow || stepDataState[key] === "missing";

      switch (config.stepKey) {
        case "step1": {
          const dirty = form.formState.dirtyFields.step1;
          const values = form.getValues("step1");
          if (!hasDirty(dirty)) {
            if (stepDataState.step1 === "sent") return;
          }

          setStepDataState(prev => ({ ...prev, step1: "sending" }));
          const payload = config.mapToApi(values);
          const id = await ensureFormId();
          if (shouldPost("step1")) {
            await FormApi.postStep1EnergyConsumption(id, payload);
          } else {
            await FormApi.upsertStep1EnergyConsumption(id, payload);
          }
          setStepDataState(prev => ({ ...prev, step1: "sent" }));
          form.resetField("step1", { defaultValue: values });
          return;
        }

        case "step2": {
          const dirty = form.formState.dirtyFields.step2;
          const values = form.getValues("step2");
          if (!hasDirty(dirty)) {
            if (stepDataState.step2 === "sent") return;
          }

          setStepDataState(prev => ({ ...prev, step2: "sending" }));
          const payload = config.mapToApi(values);
          const id = await ensureFormId();
          if (shouldPost("step2")) {
            await FormApi.postStep2OperatorAndTariff(id, payload);
          } else {
            await FormApi.upsertStep2OperatorAndTariff(id, payload);
          }
          setStepDataState(prev => ({ ...prev, step2: "sent" }));
          form.resetField("step2", { defaultValue: values });
          return;
        }

        case "step3": {
          const dirty = form.formState.dirtyFields.step3;
          const values = form.getValues("step3");
          if (!hasDirty(dirty)) {
            if (stepDataState.step3 === "sent") return;
          }

          setStepDataState(prev => ({ ...prev, step3: "sending" }));
          const payload = config.mapToApi(values);
          const id = await ensureFormId();
          if (shouldPost("step3")) {
            await FormApi.postStep3EnergyBilling(id, payload);
          } else {
            await FormApi.upsertStep3EnergyBilling(id, payload);
          }
          setStepDataState(prev => ({ ...prev, step3: "sent" }));
          form.resetField("step3", { defaultValue: values });
          return;
        }

        case "step4": {
          const dirty = form.formState.dirtyFields.step4;
          const values = form.getValues("step4");
          if (!hasDirty(dirty)) {
            if (stepDataState.step4 === "sent") return;
          }

          setStepDataState(prev => ({ ...prev, step4: "sending" }));
          const payload = config.mapToApi(values);
          const id = await ensureFormId();
          if (shouldPost("step4")) {
            await FormApi.postStep4ConnectionAndInstallation(id, payload);
          } else {
            await FormApi.upsertStep4ConnectionAndInstallation(id, payload);
          }
          setStepDataState(prev => ({ ...prev, step4: "sent" }));
          form.resetField("step4", { defaultValue: values });
          return;
        }

        case "step5": {
          const dirty = form.formState.dirtyFields.step5;
          const values = form.getValues("step5");

          const shouldPersist = hasDirty(dirty) || stepDataState.step5 !== "sent";

          setStepDataState(prev => ({ ...prev, step5: "sending" }));

          const payload = config.mapToApi(values);
          const id = await ensureFormId();

          if (shouldPersist) {
            if (!customerId) {
              const customer: FormStep5CustomerResponse = await CustomerApi.createOne(payload);
              await FormApi.update(id, customer.id);
              setCustomerId(customer.id);
            } else {
              await CustomerApi.updateOne(customerId, payload);
            }
          }

          if (!options?.skipCalculations) {
            await CalculationsApi.runCalculations(id, "free");
          }

          setStepDataState(prev => ({ ...prev, step5: "sent" }));

          if (shouldPersist) {
            form.resetField("step5", { defaultValue: values });
          }

          return;
        }

        default:
          return;
      }
    },
    onError: (_err, vars) => {
      setStepDataState(prev => ({ ...prev, [vars.stepKey]: "unknown" }));
      toast.error(t("formError"));
    },
  });
}
